import { FC, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Typography,
  useTheme,
} from '@mui/material';
import { observer } from 'mobx-react-lite';

import { routes } from '@app/routes';
import { TooltipCard } from '@components';
import { Trans, useTrans } from '@libs/i18n';
import { Assessment } from '@libs/types';
import {
  AlignBottomIcon,
  ArrowCounterClockwiseIcon,
  CopyIcon,
  DotsThreeIcon,
  ExportIcon,
  HouseIcon,
  PlusCircleIcon,
  TrashIcon,
  WarningIcon,
} from '@phosphor-icons/react';

import { useDuplicateAssessment } from '../../api';
// Deep import rather than through `../../hooks`: that barrel pulls in the
// workflow hooks, which re-enter `../../api` for mutations this header
// never uses.
import { useDuplicateTitleField } from '../../hooks/useDuplicateTitleField';
import { StartNewVersionConfirmDialog } from '../Home/StartNewVersionConfirmDialog';
import { DuplicateTitleField } from '../shared/DuplicateTitleField';

import { VersionSelector } from './VersionSelector';

interface ResultsHeaderProps {
  assessmentTitle: string;
  assessmentId: string;
  language: string;
  domainsCovered: number;
  documentsAssessed: number;
  onExportClick: () => void;
  isExportLoading?: boolean;
  onDownloadDocumentsClick?: () => void;
  isDocumentsDownloading?: boolean;
  /** When true, the "Start New Version" primary button is shown. */
  canStartNewVersion?: boolean;
  /** When true, briefly pulse the "Start New Version" button to draw attention. */
  highlightNewVersion?: boolean;
  /** Active version's assessment entity — required to open StartNewVersionConfirmDialog. */
  activeAssessment?: Assessment;
  /** All sibling versions — passed to the embedded VersionSelector. */
  allVersions?: Assessment[];
  /** Called when user picks a different version in the selector. */
  onVersionSelect?: (id: string) => void;
  /** Currently selected baseline version for comparison (undefined = none). */
  baselineVersion?: Assessment;
  /** Called when user picks or clears a comparison version. */
  onChangeCompareTo?: (versionId: string) => void;
}

export const ResultsHeader: FC<ResultsHeaderProps> = observer(({
  assessmentTitle,
  assessmentId,
  language,
  domainsCovered,
  documentsAssessed,
  onExportClick,
  isExportLoading = false,
  onDownloadDocumentsClick,
  isDocumentsDownloading = false,
  canStartNewVersion = false,
  highlightNewVersion = false,
  activeAssessment,
  allVersions,
  onVersionSelect,
  baselineVersion,
  onChangeCompareTo,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { t } = useTrans();

  const moreAnchorRef = useRef<HTMLButtonElement>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [newVersionOpen, setNewVersionOpen] = useState(false);

  const compareAnchorRef = useRef<HTMLButtonElement>(null);
  const [compareMenuOpen, setCompareMenuOpen] = useState(false);

  const compareOptions = (allVersions ?? [])
    .filter((v) => v.id !== assessmentId)
    .sort((a, b) => b.version - a.version);
  const hasBaseline = !!baselineVersion;
  const showCompareButton = compareOptions.length > 0 && !!onChangeCompareTo;

  const duplicateMutation = useDuplicateAssessment();
  const duplicateTitle = useDuplicateTitleField(assessmentTitle, confirmOpen);

  const isReassessment = !!activeAssessment && activeAssessment.version > 1;

  const isPending = duplicateMutation.isPending;

  const handleDuplicateConfirm = () => {
    if (!duplicateTitle.isValid || isPending) return;

    duplicateMutation.mutate(
      { assessmentId, title: duplicateTitle.trimmedTitle },
      {
        onSuccess: (data) => {
          setConfirmOpen(false);
          navigate(routes.assessmentDomain(data.assessmentId, data.assessmentDomainId));
        },
      },
    );
  };

  return (
    <>
      <Paper
        variant="panel"
        elevation={0}
        sx={{ border: `1px solid ${theme.palette.grey[300]}` }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          {/* Left — version/compare stack + title + subtitle */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0, flex: 1 }}>
            {/* Version selector + compare stacked vertically */}
            {(allVersions && allVersions.length > 1 && onVersionSelect) || showCompareButton ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, flexShrink: 0 }}>
                {allVersions && allVersions.length > 1 && onVersionSelect && (
                  <VersionSelector
                    versions={allVersions}
                    activeVersionId={assessmentId}
                    onSelect={onVersionSelect}
                  />
                )}
                {showCompareButton && (
                  <Button
                    ref={compareAnchorRef}
                    variant="outlined"
                    size="small"
                    startIcon={<AlignBottomIcon size={14} />}
                    onClick={() => setCompareMenuOpen(true)}
                    sx={{
                      borderRadius: '8px',
                      textTransform: 'none',
                      fontSize: '0.72rem',
                      fontWeight: 600,
                      py: 0.5,
                      px: 1.25,
                      borderColor: hasBaseline ? 'primary.light' : theme.palette.grey[300],
                      color: hasBaseline ? 'primary.main' : 'text.secondary',
                      backgroundColor: 'white',
                      justifyContent: 'flex-start',
                      '&:hover': {
                        borderColor: hasBaseline ? 'primary.main' : theme.palette.grey[400],
                        backgroundColor: hasBaseline ? `${theme.palette.primary.main}08` : 'grey.50',
                      },
                    }}
                  >
                    {hasBaseline
                      ? `${t('assessments.overview.versioning.compareTo.label')}: V${baselineVersion!.version}`
                      : t('assessments.overview.charts.addComparison')}
                  </Button>
                )}
              </Box>
            ) : null}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, minWidth: 0 }}>
              <Typography
                variant="textMD_semiBold"
                sx={{ color: 'text.primary', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
              >
                {assessmentTitle}
              </Typography>
              <Typography variant="textSM_regular" sx={{ color: 'text.tertiary' }}>
                {isReassessment ? (
                  <Trans i18nKey="assessments.overview.versioning.header.subtitle.reassessment" />
                ) : (
                  <Trans i18nKey="assessments.overview.downloadDetailedGapsOrStartNew" />
                )}
              </Typography>
            </Box>
          </Box>

          {/* Right — action buttons */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
            {/* Start New Version — only shown when eligible */}
            {canStartNewVersion && activeAssessment && (
              <TooltipCard
                variant="warning"
                placement="bottom"
                open={highlightNewVersion}
                maxWidth={280}
                description={t('assessments.overview.editDomain.startVersionHint')}
              >
                <Button
                  variant="primary"
                  startIcon={<ArrowCounterClockwiseIcon size={16} />}
                  onClick={() => setNewVersionOpen(true)}
                  sx={{
                    borderRadius: '8px',
                    backgroundColor: '#0A0A0A',
                    '&:hover': { backgroundColor: '#333' },
                    ...(highlightNewVersion && {
                      animation: 'startNewVersionPulse 0.8s ease-in-out 3',
                      '@keyframes startNewVersionPulse': {
                        '0%': { boxShadow: `0 0 0 0 ${theme.palette.primary.main}66` },
                        '70%': { boxShadow: `0 0 0 10px ${theme.palette.primary.main}00` },
                        '100%': { boxShadow: `0 0 0 0 ${theme.palette.primary.main}00` },
                      },
                    }),
                  }}
                >
                  <Trans i18nKey="assessments.overview.versioning.header.menu.startNewVersion" />
                </Button>
              </TooltipCard>
            )}

            {/* Download all documents */}
            {onDownloadDocumentsClick && (
              <Button
                variant="tertiary"
                startIcon={isDocumentsDownloading ? <CircularProgress size={14} /> : <ExportIcon size={16} />}
                onClick={onDownloadDocumentsClick}
                disabled={isDocumentsDownloading}
                sx={{ borderRadius: '8px' }}
              >
                <Trans i18nKey="app.assessment.document.downloadAll" />
              </Button>
            )}

            {/* Export Results */}
            <Button
              variant="tertiary"
              startIcon={isExportLoading ? <CircularProgress size={14} /> : <ExportIcon size={16} />}
              onClick={onExportClick}
              disabled={isExportLoading}
              sx={{ borderRadius: '8px' }}
            >
              <Trans i18nKey="assessments.overview.downloadDetailedGaps" />
            </Button>

            {/* More (···) */}
            <IconButton
              ref={moreAnchorRef}
              size="small"
              onClick={() => setMenuOpen(true)}
              sx={{
                width: 38,
                height: 38,
                borderRadius: '1rem',
                backgroundColor: theme.palette.grey[200],
                color: 'text.secondary',
                '&:hover': { backgroundColor: theme.palette.grey[600] },
              }}
            >
              <DotsThreeIcon size={18} weight="bold" />
            </IconButton>
          </Box>
        </Box>
      </Paper>

      {/* More menu */}
      <Menu
        anchorEl={moreAnchorRef.current}
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        slotProps={{
          paper: {
            elevation: 0,
            sx: {
              mt: 0.75,
              minWidth: 220,
              border: `1px solid ${theme.palette.grey[200]}`,
              borderRadius: 1.5,
              boxShadow: '0 12px 28px rgba(10,10,10,0.10), 0 2px 6px rgba(10,10,10,0.04)',
              '& .MuiList-root': { p: 0.75 },
            },
          },
        }}
      >
        <MenuItem
          component={Link}
          to={routes.assessmentList()}
          onClick={() => setMenuOpen(false)}
          sx={{ borderRadius: 1, fontSize: 13.5, fontWeight: 500, gap: 1.25, px: 1.5, py: 1.125, color: 'text.primary' }}
        >
          <HouseIcon size={16} color={theme.palette.text.secondary} />
          <Trans i18nKey="assessments.overview.viewDashboard" />
        </MenuItem>

        <MenuItem
          component={Link}
          to={routes.assessmentNew()}
          onClick={() => setMenuOpen(false)}
          sx={{ borderRadius: 1, fontSize: 13.5, fontWeight: 500, gap: 1.25, px: 1.5, py: 1.125, color: 'text.primary' }}
        >
          <PlusCircleIcon size={16} color={theme.palette.text.secondary} />
          <Trans i18nKey="assessments.overview.NewAssessment" />
        </MenuItem>

        <Divider sx={{ my: 0.5, mx: 0.5 }} />

        <MenuItem
          onClick={() => { setMenuOpen(false); setConfirmOpen(true); }}
          sx={{ borderRadius: 1, fontSize: 13.5, fontWeight: 500, gap: 1.25, px: 1.5, py: 1.125, color: 'text.primary' }}
        >
          <CopyIcon size={16} color={theme.palette.text.secondary} />
          <Trans i18nKey="assessments.overview.duplicate.menuItem" />
        </MenuItem>
      </Menu>

      {/* Start New Version dialog — from the overview, drop the user into
          the stepper for the new draft so they don't bounce back to a stale
          results screen. */}
      {activeAssessment && (
        <StartNewVersionConfirmDialog
          assessment={activeAssessment}
          open={newVersionOpen}
          onClose={() => setNewVersionOpen(false)}
          navigateToWorkflow
        />
      )}

      {/* Duplicate confirmation dialog */}
      <Dialog
        open={confirmOpen}
        onClose={isPending ? undefined : () => setConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2, overflow: 'hidden' } }}
      >
        <DialogContent sx={{ p: 0 }}>
          {isPending ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 2, p: 4 }}>
              <CircularProgress size={44} sx={{ color: theme.palette.violet[700] }} />
              <Box>
                <Typography variant="textMD_semiBold" sx={{ display: 'block', mb: 0.5 }}>
                  <Trans i18nKey="assessments.overview.duplicate.loadingTitle" />
                </Typography>
                <Typography variant="textSM_regular" sx={{ color: 'text.secondary' }}>
                  <Trans i18nKey="assessments.overview.duplicate.loadingDescription" />
                </Typography>
              </Box>
            </Box>
          ) : (
            <>
              <Box sx={{ display: 'flex', gap: 1.75, alignItems: 'flex-start', px: 3, pt: 3, pb: 1 }}>
                <Box
                  sx={{
                    width: 40, height: 40, borderRadius: '50%',
                    backgroundColor: '#FAF5FD', color: theme.palette.violet[700],
                    border: '1px solid #E8D4F8',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  }}
                >
                  <CopyIcon size={20} />
                </Box>
                <Box>
                  <Typography variant="textMD_semiBold" sx={{ display: 'block', mb: 0.25 }}>
                    <Trans i18nKey="assessments.overview.duplicate.confirmTitle" />
                  </Typography>
                  <Typography variant="textSM_regular" sx={{ color: 'text.secondary' }}>
                    <Trans i18nKey="assessments.overview.duplicate.confirmDescription" />
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ px: 3, pt: 2 }}>
                <DuplicateTitleField
                  field={duplicateTitle}
                  onSubmit={handleDuplicateConfirm}
                  size="small"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.25 } }}
                />
              </Box>

              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1.25, px: 3, py: 2 }}>
                {[
                  { label: t('assessments.overview.duplicate.statDomains'), value: domainsCovered },
                  { label: t('assessments.overview.duplicate.statDocuments'), value: documentsAssessed },
                  { label: t('assessments.overview.duplicate.statLanguage'), value: language || '—', smallText: true },
                ].map(({ label, value, smallText }) => (
                  <Box
                    key={label}
                    sx={{
                      backgroundColor: '#F8F8FA',
                      border: `1px solid ${theme.palette.grey[200]}`,
                      borderRadius: 1.25,
                      p: 1.5,
                    }}
                  >
                    <Typography variant="textXS_regular" sx={{ color: 'text.tertiary', textTransform: 'uppercase', letterSpacing: '0.04em', display: 'block' }}>
                      {label}
                    </Typography>
                    <Typography sx={{ fontSize: smallText ? 14 : 20, fontWeight: 600, mt: 0.25, pt: smallText ? 0.5 : 0, lineHeight: 1.2 }}>
                      {value}
                    </Typography>
                  </Box>
                ))}
              </Box>

              <Box
                sx={{
                  mx: 3, mb: 2,
                  display: 'flex', gap: 1.25,
                  backgroundColor: '#FFFBE6', border: '1px solid #FFE58F',
                  color: '#874D00', borderRadius: 1.25, p: 1.75,
                  fontSize: 13, lineHeight: 1.55,
                }}
              >
                <WarningIcon size={16} color="#FAAD14" style={{ flexShrink: 0, marginTop: 2 }} />
                <Typography variant="textSM_regular" sx={{ color: '#874D00' }}>
                  <Trans i18nKey="assessments.overview.duplicate.warningText" components={{ bold: <strong /> }} />
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.25, px: 3, pb: 3 }}>
                <Button variant="tertiary" onClick={() => setConfirmOpen(false)} sx={{ borderRadius: '8px' }}>
                  <Trans i18nKey="app.common.cancel" />
                </Button>
                <Button
                  variant="primary"
                  onClick={handleDuplicateConfirm}
                  disabled={!duplicateTitle.isValid}
                  startIcon={<CopyIcon size={14} />}
                  sx={{ borderRadius: '8px', backgroundColor: theme.palette.violet[700], '&:hover': { backgroundColor: '#7A2D9F' } }}
                >
                  <Trans i18nKey="assessments.overview.duplicate.confirmButton" />
                </Button>
              </Box>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Compare To menu */}
      {showCompareButton && (
        <Menu
          anchorEl={compareAnchorRef.current}
          open={compareMenuOpen}
          onClose={() => setCompareMenuOpen(false)}
          transformOrigin={{ horizontal: 'left', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
          slotProps={{
            paper: {
              elevation: 0,
              sx: {
                mt: 0.75,
                minWidth: 200,
                border: `1px solid ${theme.palette.grey[200]}`,
                borderRadius: 1.5,
                boxShadow: '0 12px 28px rgba(10,10,10,0.10), 0 2px 6px rgba(10,10,10,0.04)',
                '& .MuiList-root': { p: 0.75 },
              },
            },
          }}
        >
          {hasBaseline && (
            <MenuItem
              onClick={() => { onChangeCompareTo!(''); setCompareMenuOpen(false); }}
              sx={{ borderRadius: 1, fontSize: 13, fontWeight: 500, gap: 1.25, px: 1.5, py: 1, color: 'error.main' }}
            >
              <ListItemIcon sx={{ color: 'error.main', minWidth: 28 }}>
                <TrashIcon size={15} />
              </ListItemIcon>
              <ListItemText
                primary={t('assessments.overview.versioning.compareTo.none')}
                primaryTypographyProps={{ fontSize: 13, color: 'error.main' }}
              />
            </MenuItem>
          )}
          {compareOptions.map((v) => {
            const score = v.score != null ? Math.round(v.score) : null;
            const isSelected = v.id === baselineVersion?.id;
            return (
              <MenuItem
                key={v.id}
                selected={isSelected}
                onClick={() => { onChangeCompareTo!(v.id); setCompareMenuOpen(false); }}
                sx={{ borderRadius: 1, fontSize: 13, fontWeight: isSelected ? 600 : 500, gap: 1.25, px: 1.5, py: 1, color: 'text.primary' }}
              >
                {`Version ${v.version}${score != null ? ` (${score}%)` : ''}`}
              </MenuItem>
            );
          })}
        </Menu>
      )}
    </>
  );
});
